import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useBilling } from "../context/BillingContext";
import { api } from "../lib/api";
import { LogOut, History, BookOpen, Search, Bell, Briefcase, Coins, Crown, LayoutGrid, Home, Wand2, ShieldCheck, Footprints, Activity, MessageSquare, CalendarClock, Inbox, Store, ClipboardList } from "lucide-react";
import CreditsPopover from "./CreditsPopover";
import NavOverflowRail from "./NavOverflowMenu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isPro } = useTheme();
  const { billing } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);

  // Poll bulk unread count every 20s while logged in.
  useEffect(() => {
    if (!user || user === false) {
      setUnread(0);
      setInboxUnread(0);
      return;
    }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/bulk/unread/count");
        if (!cancelled) setUnread(data?.count || 0);
      } catch { /* ignore */ }
    };
    // Talent-Directory inbox — separate signal from bulk-tailor bell.
    const fetchInbox = async () => {
      try {
        const { data } = await api.get("/candidate/contact-requests/unread-count");
        if (!cancelled) setInboxUnread(data?.pending_count || 0);
      } catch { /* endpoint may 401 for edge cases — silently ignore */ }
    };
    fetchUnread();
    fetchInbox();
    const t = setInterval(fetchUnread, 20000);
    const t2 = setInterval(fetchInbox, 30000);
    return () => { cancelled = true; clearInterval(t); clearInterval(t2); };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Labels — kept short so the navbar fits the admin "Beta" pill on standard
  // laptop widths. "Tips" replaces the old "R & D" label per user request.
  const labels = {
    jobSearch: "Jobs",
    skillLab: "Skill Lab",
    resources: "Tips",
    history: "History",
  };

  const isActive = (path, opts = {}) => {
    if (opts.startsWith) return location.pathname.startsWith(path);
    return location.pathname === path;
  };

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md"
      style={{
        background: "rgba(253, 251, 247, 0.92)",
        borderBottom: isPro ? "2px solid #0F1B2D" : "4px solid #0F1B2D",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          {/* Resmo wordmark always anchored by the mascot on its left */}
          <img
            src="/brand/resmo-character.png"
            alt=""
            className="rm-shadow-bob"
            style={{ width: 46, height: 46, objectFit: "contain", flexShrink: 0 }}
            draggable={false}
          />
          <div className="flex flex-col">
            <div className="font-display text-2xl md:text-3xl flex items-baseline leading-none" style={{ letterSpacing: "-0.02em" }}>
              {isPro ? (
                <>
                  <span style={{ color: "#0F1B2D" }}>Pro</span>
                  <span className="rm-text-orange">Mo</span>
                </>
              ) : (
                <>
                  <span className="rm-text-orange">R</span>
                  <span style={{ color: "#0F1B2D" }}>esmo</span>
                </>
              )}
            </div>
            <div
              className="font-mono-rm text-[10px] md:text-[11px] uppercase tracking-[0.18em] mt-1 leading-none"
              style={{ color: "#475569" }}
              data-testid="navbar-tagline"
            >
              {isPro
                ? "Career Strategy · Senior Professionals"
                : "Your Career Intelligence Companion"}
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-2 md:gap-2">
          {user ? (
            <>
              {/* Anchor pills — always visible, never overflow.
                  Order: Welcome (intro/about) → Dashboard (home base). */}
              <Link
                to="/welcome"
                className={`inline-flex rm-nav-pill ${isActive("/welcome") ? "is-active" : ""}`}
                data-testid="nav-welcome"
                title="Welcome page · About Resmo"
              >
                <Home size={14} />
                <span className="hidden sm:inline">Welcome</span>
              </Link>
              <Link
                to="/"
                className={`inline-flex rm-nav-pill ${location.pathname === "/" ? "is-active" : ""}`}
                data-testid="nav-dashboard"
                title="Dashboard · Your career command center — every phase, every tool, one screen"
              >
                <LayoutGrid size={14} />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>

              {/* Moveable pills — automatically collapse into a ⋯ More popover
                  if the row gets crowded. `priority` controls which pills
                  drop first: higher = stays visible longer. */}
              <NavOverflowRail
                testid="nav-rail"
                items={[
                  {
                    id: "studio",
                    to: "/studio",
                    label: "Studio",
                    icon: Wand2,
                    title: "Resume Studio · ATS-friendly templates + tweaks",
                    priority: 70,
                    isActive: isActive("/studio"),
                  },
                  {
                    id: "job-search",
                    to: "/job-search",
                    label: labels.jobSearch,
                    icon: isPro ? Briefcase : Search,
                    title: "Jobs · Hunt live openings from Adzuna with one-click tailor",
                    priority: 75,
                    isActive: isActive("/job-search"),
                  },
                  {
                    id: "network",
                    to: "/network",
                    label: "Network",
                    icon: Footprints,
                    title: "Network · Stepping Stones — pitches, people, sparks, mindset",
                    priority: 65,
                    isActive: isActive("/network"),
                  },
                  {
                    id: "interviews",
                    to: "/interviews",
                    label: "Interviews",
                    icon: CalendarClock,
                    title: "Interview Mode · Schedule and prep for upcoming interviews",
                    priority: 60,
                    isActive: isActive("/interviews", { startsWith: true }),
                  },
                  {
                    id: "tracker",
                    to: "/applications",
                    label: "Tracker",
                    icon: ClipboardList,
                    title: "Application Tracker · every job you applied to — status + follow-ups",
                    priority: 58,
                    isActive: isActive("/applications"),
                  },
                  ...(user?.is_recruiter ? [{
                    id: "recruiter",
                    to: "/recruiter",
                    label: "Recruiter",
                    icon: Crown,
                    title: "Resmo Pro · Talent Cloud — recruiter desk",
                    priority: 80,
                    isActive: isActive("/recruiter", { startsWith: true }),
                    style: { color: "#0A2540", borderColor: "#047857" },
                  }] : []),
                  {
                    id: "history",
                    to: "/history",
                    label: labels.history,
                    icon: History,
                    title: "History · Browse every tailored resume, assessment, and analysis you've run",
                    priority: 55,
                    isActive: isActive("/history"),
                  },
                  ...(user?.is_admin ? [{
                    id: "admin-beta",
                    to: "/admin/beta-invites",
                    label: "Beta",
                    icon: ShieldCheck,
                    title: "Admin · Manage beta tester invites",
                    priority: 40,
                    isActive: isActive("/admin/beta", { startsWith: true }),
                    style: { borderColor: "#FFC145" },
                    iconClassName: "rm-text-orange",
                  }] : []),
                  ...(user?.is_admin ? [{
                    id: "admin-activity",
                    to: "/admin/activity",
                    label: "Activity",
                    icon: Activity,
                    title: "Admin · See who has been on the site and where they went",
                    priority: 35,
                    isActive: isActive("/admin/activity"),
                    style: { borderColor: "#FFC145" },
                    iconClassName: "rm-text-orange",
                  }] : []),
                  ...(user?.is_admin ? [{
                    id: "admin-feedback",
                    to: "/admin/feedback",
                    label: "Feedback",
                    icon: MessageSquare,
                    title: "Admin · Feedback grouped by page",
                    priority: 36,
                    isActive: isActive("/admin/feedback"),
                    style: { borderColor: "#FFC145" },
                    iconClassName: "rm-text-orange",
                  }] : []),
                  ...(user?.is_admin ? [{
                    id: "admin-employers",
                    to: "/admin/employers",
                    label: "Employers",
                    icon: Briefcase,
                    title: "Admin · Verify employer signups for the Talent Directory",
                    priority: 38,
                    isActive: isActive("/admin/employers"),
                    style: { borderColor: "#FFC145" },
                    iconClassName: "rm-text-orange",
                  }] : []),
                  // Admin can browse the Candidates Store as a shadow employer
                  // (backend synthesises a virtual verified employer from the
                  // admin JWT). Placed right after "Employers" so both admin +
                  // employer surfaces sit next to each other in the nav.
                  ...(user?.is_admin ? [{
                    id: "admin-store",
                    to: "/employer/store",
                    label: "Store",
                    icon: Store,
                    title: "Admin · Browse the Candidates Store as a shadow employer",
                    priority: 37,
                    isActive: isActive("/employer/store"),
                    style: { borderColor: "#FFC145" },
                    iconClassName: "rm-text-orange",
                  }] : []),
                  ...(user?.is_admin ? [{
                    id: "resources",
                    to: "/resources",
                    label: labels.resources,
                    icon: BookOpen,
                    title: "Tips · Resume + interview templates and a live job-market pulse",
                    priority: 25,
                    isActive: isActive("/resources"),
                  }] : []),
                ]}
              />

              <CreditsPopover billing={billing}>
                <Link
                  to="/billing"
                  className={`inline-flex rm-nav-pill ${isActive("/billing") ? "is-active" : ""} ${(!billing?.is_pro && (billing?.credits_balance ?? 0) < 25) ? "rm-pulse-low" : ""}`}
                  data-testid="nav-billing"
                  title={
                    billing?.is_pro
                      ? "Pro · Manage subscription"
                      : (billing?.credits_balance ?? 0) < 25
                        ? `Low credits (${billing?.credits_balance ?? 0}) — hover for one-click top-up`
                        : "Credits · Hover for one-click top-up"
                  }
                  style={
                    !billing?.is_pro && (billing?.credits_balance ?? 0) < 25
                      ? { borderColor: "#DC2626", color: "#DC2626" }
                      : undefined
                  }
                >
                  {billing?.is_pro ? (
                    <>
                      <Crown size={14} className="rm-text-orange" />
                      <span className="hidden sm:inline">Pro</span>
                    </>
                  ) : (
                    <>
                      <Coins
                        size={14}
                        style={(billing?.credits_balance ?? 0) < 25 ? { color: "#DC2626" } : undefined}
                        className={(billing?.credits_balance ?? 0) < 25 ? "" : "rm-text-orange"}
                      />
                      <span data-testid="nav-credit-count">{billing?.credits_balance ?? "·"}</span>
                      <span className="hidden sm:inline text-[10px] opacity-80">credits</span>
                    </>
                  )}
                </Link>
              </CreditsPopover>
              <Link
                to="/history"
                className={`hidden md:inline-flex rm-nav-pill is-icon-only ${isActive("/history") ? "is-active" : ""}`}
                data-testid="nav-bell"
                title={unread > 0 ? `${unread} bulk batch${unread === 1 ? "" : "es"} just finished` : "Notifications & history"}
                aria-label="Notifications"
                style={{ position: "relative" }}
              >
                <Bell size={14} />
                {unread > 0 && (
                  <span
                    data-testid="nav-bell-dot"
                    style={{
                      position: "absolute",
                      top: 4, right: 4,
                      minWidth: 14, height: 14, borderRadius: 7,
                      background: "#DC2626",
                      border: "2px solid #FDFBF7",
                      color: "white",
                      fontSize: 9, fontWeight: 700,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      padding: "0 4px",
                      lineHeight: 1,
                    }}
                  >
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </Link>
              {/* Talent-Directory inbox — appears with a red dot when a
                  verified employer has reached out. Hidden on small screens
                  so the nav doesn't overflow. */}
              <Link
                to="/requests"
                className={`hidden md:inline-flex rm-nav-pill is-icon-only ${isActive("/requests") ? "is-active" : ""}`}
                data-testid="nav-inbox"
                title={inboxUnread > 0
                  ? `${inboxUnread} employer${inboxUnread === 1 ? "" : "s"} want to talk`
                  : "Talent Directory inbox"}
                aria-label="Talent Directory inbox"
                style={{ position: "relative" }}
              >
                <Inbox size={14} />
                {inboxUnread > 0 && (
                  <span
                    data-testid="nav-inbox-dot"
                    style={{
                      position: "absolute",
                      top: 4, right: 4,
                      minWidth: 14, height: 14, borderRadius: 7,
                      background: "#FF6A00",
                      border: "2px solid #FDFBF7",
                      color: "white",
                      fontSize: 9, fontWeight: 700,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      padding: "0 4px",
                      lineHeight: 1,
                    }}
                  >
                    {inboxUnread > 9 ? "9+" : inboxUnread}
                  </span>
                )}
              </Link>
              <button
                onClick={handleLogout}
                className="rm-nav-pill is-icon-only"
                data-testid="nav-logout-btn"
                title="Log out"
                aria-label="Log out"
              >
                <LogOut size={14} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="rm-btn rm-btn-ghost py-2 px-3 text-xs" data-testid="nav-login-btn">
                Login
              </Link>
              <Link
                to="/register"
                className="rm-btn py-2 px-3 text-xs"
                data-testid="nav-register-btn"
                style={{
                  background: "transparent",
                  color: "#0F1B2D",
                  border: "2px solid #0F1B2D",
                  boxShadow: "2px 2px 0 0 #0F1B2D",
                }}
              >
                Sign up →
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
